import React from "react";
import { Modal, Form, Button, Row, Col, Badge } from "react-bootstrap";
import { planModuleCategories } from "../../../data/planModuleCategories";
import { Check } from "lucide-react";

const LIMIT_FIELDS = [
  { key: "maxStudentsPerBatch", label: "Max Students Per Batch", hint: "0 = Unlimited" },
  { key: "maxBatches", label: "Max Active Batches", hint: "0 = Unlimited" },
  { key: "maxCourses", label: "Max Active Courses", hint: "0 = Unlimited" },
  { key: "maxInstructors", label: "Max Instructors", hint: "0 = Unlimited" },
  { key: "maxAdmins", label: "Max Admins", hint: "0 = Unlimited" },
  { key: "storageGB", label: "Storage (GB)", hint: "0 = Unlimited" },
  { key: "maxConcurrentMeetings", label: "Max Concurrent Zoom Classes", hint: "0 = Disabled" },
  { key: "maxConcurrentZohoMeetings", label: "Max Concurrent Zoho Classes", hint: "0 = Disabled. Requires Platform-hosted Zoho toggle." },
  { key: "maxBranches", label: "Max Branches", hint: "0 = Unlimited, 1 = Default only" },
  { key: "aiWhatsappReplyMonthlyQuota", label: "AI WhatsApp Replies / Month", hint: "0 = Disabled, -1 = Unlimited", allowNegative: true },
];

/**
 * PlanFormModal — Create/Edit a subscription plan.
 */
const PlanFormModal = ({ show, onHide, onSubmit, formData, setFormData, loading, isEdit = false }) => {
  const handleChange = (field) => (e) => {
    const val = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: val }));
  };

  const handlePriceChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: parseFloat(e.target.value) || 0 }));
  };

  const handleNumberChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: parseInt(e.target.value) || 0 }));
  };

  const handleToggle = (field) => () => {
    setFormData((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-6 fw-semibold">
          {isEdit ? "Edit Plan" : "Create Plan"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label className="small fw-medium">Plan Name *</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.name || ""}
                  onChange={handleChange("name")}
                  required
                  placeholder="e.g. Pro"
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Pricing */}
          <h6 className="mt-4 mb-2" style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-tertiary)" }}>
            Pricing (₹)
          </h6>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small fw-medium">Monthly</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  value={formData.priceMonthly ?? ""}
                  onChange={handlePriceChange("priceMonthly")}
                  placeholder="₹/month"
                  min={0}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small fw-medium">Quarterly</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  value={formData.priceQuarterly ?? ""}
                  onChange={handlePriceChange("priceQuarterly")}
                  placeholder="₹/quarter"
                  min={0}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small fw-medium">Yearly</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  value={formData.priceYearly ?? ""}
                  onChange={handlePriceChange("priceYearly")}
                  placeholder="₹/year"
                  min={0}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Revenue & Storage Pricing */}
          <h6 className="mt-4 mb-2" style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-tertiary)" }}>
            Revenue & Storage
          </h6>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label className="small fw-medium d-block">Revenue Share Mode</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    id="rs-mode-pct"
                    name="revenueShareType"
                    label="Percentage of fee"
                    checked={(formData.revenueShareType || "percentage") === "percentage"}
                    onChange={() => setFormData((prev) => ({ ...prev, revenueShareType: "percentage" }))}
                  />
                  <Form.Check
                    type="radio"
                    id="rs-mode-flat"
                    name="revenueShareType"
                    label="Flat ₹ per active student"
                    checked={formData.revenueShareType === "flat"}
                    onChange={() => setFormData((prev) => ({ ...prev, revenueShareType: "flat" }))}
                  />
                </div>
              </Form.Group>
            </Col>
            {(formData.revenueShareType || "percentage") === "percentage" ? (
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-medium">Revenue Share (%)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    value={formData.revenueSharePercent ?? ""}
                    onChange={handlePriceChange("revenueSharePercent")}
                    placeholder="e.g. 5"
                    min={0}
                    max={100}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>
                    % of fee-collection revenue shared with platform
                  </Form.Text>
                </Form.Group>
              </Col>
            ) : (
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-medium">Flat Amount (₹ per active student)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    value={formData.revenueShareFlatAmount ?? ""}
                    onChange={handlePriceChange("revenueShareFlatAmount")}
                    placeholder="e.g. 20"
                    min={0}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>
                    Fixed ₹ charged per (student, course) line each month
                  </Form.Text>
                </Form.Group>
              </Col>
            )}
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-medium">Extra Storage Price (per 100 GB)</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  value={formData.extraStoragePricePer100GB ?? ""}
                  onChange={handlePriceChange("extraStoragePricePer100GB")}
                  placeholder="₹/100 GB"
                  min={0}
                />
                <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>
                  Charged when institute exceeds the storage limit
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-medium">Min Active Days Before Billing</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  value={formData.minActiveDaysForBilling ?? ""}
                  onChange={handleNumberChange("minActiveDaysForBilling")}
                  placeholder="e.g. 5"
                  min={0}
                  max={90}
                />
                <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>
                  Batch students are billed only after this many days from activation. 0 = bill immediately.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label className="small fw-medium d-block">Billing Cadence</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    id="rs-cadence-once"
                    name="revenueShareCadence"
                    label="Once per student (one-time commission)"
                    checked={(formData.revenueShareCadence || "once") === "once"}
                    onChange={() => setFormData((prev) => ({ ...prev, revenueShareCadence: "once" }))}
                  />
                  <Form.Check
                    type="radio"
                    id="rs-cadence-monthly"
                    name="revenueShareCadence"
                    label="Monthly (recurring while active)"
                    checked={formData.revenueShareCadence === "monthly"}
                    onChange={() => setFormData((prev) => ({ ...prev, revenueShareCadence: "monthly" }))}
                  />
                </div>
                <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>
                  "Once" bills each (student, course) exactly once. "Monthly" re-bills every period the student is active.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Check
                  type="switch"
                  id="rs-allow-without-batch"
                  label="Allow revenue sharing without batch"
                  checked={!!formData.allowBillingWithoutBatch}
                  onChange={() => setFormData((prev) => ({ ...prev, allowBillingWithoutBatch: !prev.allowBillingWithoutBatch }))}
                />
                <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>
                  Enable for institutes that don't use batches (e.g. coding-platform-only). Uses join date as activation.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Feature Limits */}
          <h6 className="mt-4 mb-2" style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-tertiary)" }}>
            Feature Limits <span style={{ fontWeight: 400, textTransform: "none", fontSize: "0.7rem" }}>(max active at any time)</span>
          </h6>
          <Row className="g-3">
            {LIMIT_FIELDS.map((f) => (
              <Col md={4} key={f.key}>
                <Form.Group>
                  <Form.Label className="small fw-medium">{f.label}</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    value={formData[f.key] ?? ""}
                    onChange={handleNumberChange(f.key)}
                    placeholder={f.hint}
                    min={f.allowNegative ? undefined : 0}
                  />
                  {f.hint && <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>{f.hint}</Form.Text>}
                </Form.Group>
              </Col>
            ))}
          </Row>

          {/* ── Module Access — organised by category like admin permissions ── */}
          <h6 className="mt-4 mb-2" style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-tertiary)" }}>
            Module Access
          </h6>

          {planModuleCategories.map((cat) => {
            const Icon = cat.icon;
            const isCore = !!cat.core;
            const allOn = cat.modules.length > 0 && cat.modules.every((m) => !!formData[m.key]);

            /* Toggle all modules in this category at once */
            const handleToggleAll = () => {
              if (!cat.modules.length) return;
              const nextVal = !allOn;
              setFormData((prev) => {
                const next = { ...prev };
                cat.modules.forEach((m) => { next[m.key] = nextVal; });
                return next;
              });
            };

            return (
              <div
                key={cat.category}
                className="mb-3"
                style={{
                  border: "1px solid var(--border-color, #e5e7eb)",
                  borderRadius: 8,
                  padding: "0.75rem 1rem",
                  background: isCore ? "var(--bg-subtle, #f9fafb)" : undefined,
                }}
              >
                {/* Category header */}
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <div className="d-flex align-items-center gap-2">
                    {Icon && <Icon size={16} style={{ color: "var(--primary, #2563eb)" }} />}
                    <span style={{ fontSize: "0.8125rem", fontWeight: 600 }}>{cat.category}</span>
                    {isCore && (
                      <Badge bg="success" pill style={{ fontSize: "0.65rem", fontWeight: 500 }}>
                        Always Included
                      </Badge>
                    )}
                  </div>
                  {/* Select all toggle for non‑core categories */}
                  {!isCore && cat.modules.length > 1 && (
                    <Form.Check
                      type="switch"
                      label={<span style={{ fontSize: "0.75rem" }}>All</span>}
                      checked={allOn}
                      ref={undefined}
                      onChange={handleToggleAll}
                      style={{ fontSize: "0.75rem" }}
                    />
                  )}
                </div>
                <p style={{ fontSize: "0.7rem", color: "var(--text-tertiary, #9ca3af)", margin: 0 }}>
                  {cat.description}
                </p>

                {/* Core modules — info only */}
                {isCore && cat.limitKeys && (
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {cat.limitKeys.map((lk) => {
                      const lf = LIMIT_FIELDS.find((f) => f.key === lk);
                      return lf ? (
                        <span
                          key={lk}
                          style={{ fontSize: "0.7rem", color: "var(--text-secondary, #6b7280)" }}
                        >
                          <Check size={12} className="me-1" style={{ color: "#22c55e" }} />
                          {lf.label}: <strong>{formData[lk] || "Unlimited"}</strong>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}

                {/* Toggleable modules */}
                {!isCore && cat.modules.length > 0 && (
                  <div className="mt-2">
                    {cat.modules.map((m) => (
                      <div
                        key={m.key}
                        className="d-flex align-items-start justify-content-between py-1"
                        style={{ borderTop: "1px solid var(--border-color, #f3f4f6)" }}
                      >
                        <div>
                          <span style={{ fontSize: "0.8125rem", fontWeight: 500 }}>{m.label}</span>
                          {m.description && (
                            <p style={{ fontSize: "0.7rem", color: "var(--text-tertiary, #9ca3af)", margin: 0 }}>
                              {m.description}
                            </p>
                          )}
                        </div>
                        <Form.Check
                          type="switch"
                          checked={!!formData[m.key]}
                          onChange={handleToggle(m.key)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Metadata */}
          <h6 className="mt-4 mb-2" style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-tertiary)" }}>
            Metadata
          </h6>
          <div className="d-flex flex-wrap gap-3">
            <Form.Check
              type="switch"
              label="Mark as Popular"
              checked={!!formData.isPopular}
              onChange={handleToggle("isPopular")}
              style={{ fontSize: "0.8125rem" }}
            />
            <Form.Check
              type="switch"
              label="Active"
              checked={formData.isActive !== false}
              onChange={handleToggle("isActive")}
              style={{ fontSize: "0.8125rem" }}
            />            <Form.Check
              type="switch"
              label="Public (visible in upgrade page)"
              checked={formData.isPublic !== false}
              onChange={handleToggle("isPublic")}
              style={{ fontSize: "0.8125rem" }}
            />
            <Form.Check
              type="switch"
              label="Platform-hosted Zoho (LMS-owned account)"
              checked={!!formData.platformHostedZoho}
              onChange={handleToggle("platformHostedZoho")}
              style={{ fontSize: "0.8125rem" }}
              title="When enabled, institutes on this plan use the SaaS-owned Zoho account instead of needing their own."
            />          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" size="sm" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="submit" disabled={loading}>
            {loading ? "Saving…" : isEdit ? "Update Plan" : "Create Plan"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PlanFormModal;
